/**
 * feat-109 Slice A — the detail pane's Type segmented control.
 *
 * Offers Multiple choice / Fill blank / Rehearsal / Video (feat-165) so the admin
 * picks the item type IN the form (paired with the single `+ Add` affordance in
 * DrillChildList). Choosing Rehearsal swaps the detail pane to the rehearsal field
 * set; Video swaps to the VideoDetailForm; MC/Fill shows the question form. Used by
 * QuestionDetailForm (create mode) and the new-rehearsal / new-video panes in
 * QuizContentManagement so all surfaces share one control and one set of labels.
 */

import React from 'react';
import type { QuestionType } from '../../../services/quizContentAdminService';

/** The item types the Type control offers. */
export type DetailItemType = QuestionType | 'rehearsal' | 'video' | 'reading';

const OPTIONS: { value: DetailItemType; label: string }[] = [
  { value: 'multiple_choice', label: 'Multiple choice' },
  { value: 'fill_blank', label: 'Fill blank' },
  { value: 'rehearsal', label: 'Rehearsal' },
  { value: 'video', label: 'Video' }, // feat-165 explanatory video item
  { value: 'reading', label: 'Reading' }, // feat-166 reading item
];

export interface DetailTypeControlProps {
  value: DetailItemType;
  onChange: (next: DetailItemType) => void;
  disabled?: boolean;
}

export const DetailTypeControl: React.FC<DetailTypeControlProps> = ({
  value,
  onChange,
  disabled,
}) => (
  <div>
    <label className="block text-xs font-bold text-inkMuted mb-1.5">Type</label>
    <div className="inline-flex bg-gray-100 rounded-full p-0.5 text-xs font-semibold">
      {OPTIONS.map((opt) => {
        const active = value === opt.value;
        return (
          <button
            key={opt.value}
            type="button"
            disabled={disabled}
            aria-pressed={active}
            onClick={() => onChange(opt.value)}
            className={`rounded-full px-3 py-1.5 disabled:opacity-50 ${
              active ? 'bg-white text-ink shadow-sm' : 'text-inkMuted'
            }`}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  </div>
);

export default DetailTypeControl;
